import { useState, useEffect, useMemo } from 'react';
import { Button, Spinner, ProgressBar } from 'react-bootstrap';
import { Info, CheckCircle2, CheckSquare, Square, AlertCircle } from 'lucide-react';

const InstructionsStep = ({
    currentStep,
    loading,
    handleVatPeriodClose,
    undoingClose,
    canGoToNextStep,
    goToNextStep,
    markStepComplete
}) => {
    const StepEnum = {
        INSTRUCTIONS: 'instructions'
    };

    // Checklist items based on Swedish VAT reporting requirements
    const checklistItems = useMemo(() => [
        {
            id: 'customer_invoices',
            label: 'Alla kundfakturor för perioden är bokförda',
            description: 'Kontrollera att alla utgående fakturor är registrerade'
        },
        {
            id: 'supplier_invoices',
            label: 'Alla leverantörsfakturor för perioden är bokförda',
            description: 'Säkerställ att alla inkommande fakturor och kvitton är registrerade'
        },
        {
            id: 'payments_reconciled',
            label: 'Alla betalningar är avstämda',
            description: 'Både inkommande och utgående betalningar ska vara bokförda'
        },
        {
            id: 'bank_reconciliation',
            label: 'Bankavstämning är genomförd',
            description: 'Kontrollera att bankkontona stämmer överens med bokföringen'
        },
        {
            id: 'previous_period',
            label: 'Föregående momsperiod är rapporterad',
            description: 'Den tidigare perioden ska vara slutförd och inlämnad till Skatteverket'
        },
        {
            id: 'vat_accounts',
            label: 'Momskonton är granskade',
            description: 'Kontrollera att alla momskonton (2610, 2620, 2630, 2640, 2650) är korrekta'
        },
        {
            id: 'documentation',
            label: 'Alla verifikationer har underlag',
            description: 'Fakturor och kvitton ska finnas som underlag (sparas minst 7 år)'
        },
        {
            id: 'period_closed',
            label: 'Bokföringsperioden är avslutad',
            description: 'Inga fler transaktioner för denna period ska bokföras'
        }
    ], []);

    const [checkedItems, setCheckedItems] = useState({});
    const [allChecked, setAllChecked] = useState(false);

    useEffect(() => {
        const allItemsChecked = checklistItems.every(item => checkedItems[item.id]);
        setAllChecked(allItemsChecked);
    }, [checkedItems, checklistItems]);

    const handleCheckItem = (id) => {
        setCheckedItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleCheckAll = () => {
        if (allChecked) {
            // Uncheck all
            setCheckedItems({});
        } else {
            // Check all
            const newChecked = {};
            checklistItems.forEach(item => {
                newChecked[item.id] = true;
            });
            setCheckedItems(newChecked);
        }
    };

    const checkedCount = Object.values(checkedItems).filter(Boolean).length;
    const progress = (checkedCount / checklistItems.length) * 100;

    return (
        <div className="step-container">
            <style>{`
                .checklist-item {
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 0.75rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .checklist-item:hover {
                    border-color: #3b82f6;
                    background: #f8fafc;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }

                .checklist-item.checked {
                    background: #f0fdf4;
                    border-color: #10b981;
                }

                .checklist-item.checked:hover {
                    background: #dcfce7;
                }

                .checklist-checkbox {
                    width: 24px;
                    height: 24px;
                    min-width: 24px;
                    color: #10b981;
                }

                .checklist-label {
                    font-weight: 500;
                    color: #1e293b;
                    margin-bottom: 0.25rem;
                }

                .checklist-description {
                    font-size: 0.875rem;
                    color: #64748b;
                    margin: 0;
                }

                .progress-section {
                    background: #f8fafc;
                    border-radius: 8px;
                    padding: 1.25rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #e5e7eb;
                }

                .progress-label {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #64748b;
                    margin-bottom: 0.5rem;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }

                .check-all-btn {
                    border-radius: 6px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                }

                .check-all-btn:hover {
                    transform: translateY(-1px);
                }

                .info-card {
                    background: #fffbeb;
                    border: 1px solid #fde68a;
                    border-left: 4px solid #f59e0b;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-top: 1.5rem;
                }
            `}</style>

            <div className="step-header">
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: '#3b82f6',
                            color: 'white'
                        }}
                    >
                        <Info size={24} />
                    </div>
                    <div>
                        <h3 className="mb-1">Förberedelser</h3>
                        <p className="text-muted mb-0">Kontrollera att allt är klart före momsrapportering</p>
                    </div>
                </div>
            </div>

            <div className="step-content">
                {/* Progress section */}
                <div className="progress-section">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="progress-label">Framsteg</span>
                        <span className="fw-bold" style={{ color: allChecked ? '#10b981' : '#3b82f6' }}>
                            {checkedCount} / {checklistItems.length}
                        </span>
                    </div>
                    <ProgressBar
                        now={progress}
                        variant={allChecked ? 'success' : 'primary'}
                        style={{ height: '8px', borderRadius: '4px' }}
                    />
                    <div className="mt-3">
                        <Button
                            variant={allChecked ? 'outline-success' : 'outline-primary'}
                            size="sm"
                            onClick={handleCheckAll}
                            className="check-all-btn d-inline-flex align-items-center"
                        >
                            {allChecked ? (
                                <>
                                    <CheckSquare size={16} className="me-2" />
                                    Avmarkera alla
                                </>
                            ) : (
                                <>
                                    <Square size={16} className="me-2" />
                                    Markera alla
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {/* Checklist */}
                <div className="mb-3">
                    <h6 className="mb-3 fw-semibold">Kontrollista innan momsrapportering:</h6>
                    {checklistItems.map(item => (
                        <div
                            key={item.id}
                            className={`checklist-item ${checkedItems[item.id] ? 'checked' : ''}`}
                            onClick={() => handleCheckItem(item.id)}
                        >
                            <div className="d-flex align-items-start">
                                <div className="checklist-checkbox me-3">
                                    {checkedItems[item.id] ? (
                                        <CheckSquare size={24} />
                                    ) : (
                                        <Square size={24} style={{ color: '#cbd5e1' }} />
                                    )}
                                </div>
                                <div className="flex-grow-1">
                                    <div className="checklist-label">{item.label}</div>
                                    <p className="checklist-description">{item.description}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Info card */}
                <div className="info-card">
                    <div className="d-flex align-items-start">
                        <AlertCircle size={20} className="text-warning me-2 mt-1 flex-shrink-0" />
                        <div>
                            <strong className="d-block mb-1">Viktigt att veta:</strong>
                            <p className="mb-2 small">
                                Enligt Skatteverkets regler måste alla verifikationer och underlag sparas i minst 7 år.
                                Momsdeklarationen ska innehålla information om momspliktig försäljning, utgående moms,
                                inköp med omvänd skattskyldighet, import och ingående moms.
                            </p>
                            <p className="mb-0 small text-muted">
                                <strong>Har du redan bokfört momsen manuellt?</strong> Du kan stänga perioden direkt genom att klicka på "Stäng period" nedan.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="step-actions">
                {!allChecked && (
                    <div className="alert alert-info mb-3" style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                        <div className="d-flex align-items-center">
                            <Info size={18} className="text-primary me-2" />
                            <span className="small">Markera alla punkter i checklistan för att fortsätta till nästa steg.</span>
                        </div>
                    </div>
                )}

                <div className="d-flex gap-2">
                    {allChecked && canGoToNextStep && (
                        <Button
                            variant="primary"
                            onClick={async () => {
                                await markStepComplete('instructions');
                                goToNextStep();
                            }}
                            className="d-flex align-items-center"
                        >
                            <CheckCircle2 size={16} className="me-2" />
                            Fortsätt till nästa steg
                        </Button>
                    )}

                    <Button
                        variant="outline-secondary"
                        size={allChecked ? 'md' : 'sm'}
                        onClick={handleVatPeriodClose}
                        disabled={loading || undoingClose}
                        className="d-flex align-items-center"
                    >
                        <CheckCircle2 size={16} className="me-2" />
                        Stäng period (redan bokförd)
                        {(loading || undoingClose) && (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="ms-2"
                            />
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default InstructionsStep;